import React, { useEffect, useMemo, useRef, useState } from "react";
import { Film, RefreshCw, Trash2, UploadCloud } from "lucide-react";
import { AppButton, Callout, ConfirmModal, DropZone, ProgressBar } from "../ui";
import {
  PREVIEW_VIDEO_ACCEPT,
  PREVIEW_VIDEO_MAX_MB,
  previewVideoProblem,
  previewVideoUploadError,
  removeCoursePreviewVideo,
  uploadCoursePreviewVideo,
} from "../../utils/coursePreviewVideoApi";

const formatDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  if (total <= 0) return "";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const formatSize = (bytes) => {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  if (mb <= 0) return "";
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`;
};

/**
 * CoursePreviewVideoField — the free preview clip on a course's website page.
 *
 * On a saved course a picked file uploads at once and is live when it
 * finishes: a video takes minutes, and holding it for the form's Save would
 * put a stalled upload in front of every other field. A course that does not
 * exist yet has nothing to attach it to, so the file goes to the page through
 * `onPendingFileChange` and the page uploads it after creating the course.
 *
 * Props:
 *   courseId            — the saved course, or undefined while adding one
 *   video               — the saved preview (GET /courses/:id → previewVideo)
 *   onVideoChange       — (video | null) after an upload or a removal
 *   pendingFile         — add mode: the file waiting for the course to exist
 *   onPendingFileChange — add mode: (File | null)
 *   disabled            — e.g. while the course form is saving
 */
export default function CoursePreviewVideoField({
  courseId,
  video,
  onVideoChange,
  pendingFile,
  onPendingFileChange,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [upload, setUpload] = useState(null); // { name, size, percent }
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [playerDuration, setPlayerDuration] = useState(0);

  // Lets the admin play a file back before the course it belongs to exists.
  const pendingUrl = useMemo(() => (pendingFile ? URL.createObjectURL(pendingFile) : ""), [pendingFile]);
  useEffect(() => () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
  }, [pendingUrl]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Closing the tab mid-upload would silently throw the upload away.
  useEffect(() => {
    if (!upload) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [upload]);

  const busy = Boolean(upload) || removing || disabled;
  const current = pendingFile
    ? { url: pendingUrl, fileName: pendingFile.name, sizeBytes: pendingFile.size, durationSec: 0 }
    : video;

  const openPicker = () => inputRef.current?.click();

  const pick = async (file) => {
    if (!file || busy) return;
    const problem = previewVideoProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setPlayerDuration(0);

    if (!courseId) {
      onPendingFileChange?.(file);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setUpload({ name: file.name, size: file.size, percent: 0 });
    try {
      const saved = await uploadCoursePreviewVideo(courseId, file, {
        signal: controller.signal,
        onProgress: (percent) => setUpload((u) => (u ? { ...u, percent } : u)),
      });
      onVideoChange?.(saved);
    } catch (err) {
      if (!controller.signal.aborted) setError(previewVideoUploadError(err));
    } finally {
      abortRef.current = null;
      setUpload(null);
    }
  };

  const handleRemove = () => {
    setError("");
    if (pendingFile) onPendingFileChange?.(null);
    else setConfirmRemove(true);
  };

  const confirmRemoval = async () => {
    setRemoving(true);
    try {
      await removeCoursePreviewVideo(courseId);
      onVideoChange?.(null);
      setConfirmRemove(false);
    } catch (err) {
      setConfirmRemove(false);
      setError(err?.response?.data?.message || "Could not remove the preview video.");
    } finally {
      setRemoving(false);
    }
  };

  const meta = current
    ? [formatDuration(current.durationSec || playerDuration), formatSize(current.sizeBytes)].filter(Boolean).join(" · ")
    : "";

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {upload ? (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-p-3">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-text-content">
            <Film size={16} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
            <span className="tw-min-w-0 tw-flex-1 tw-truncate" title={upload.name}>{upload.name}</span>
            <span className="tw-shrink-0 tw-text-xs tw-text-content-subtle">{formatSize(upload.size)}</span>
          </div>
          <div
            role="progressbar"
            aria-label="Uploading the preview video"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={upload.percent}
          >
            <ProgressBar value={upload.percent} size="md" />
          </div>
          <div className="tw-flex tw-min-h-7 tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-xs tw-text-content-muted">
              {upload.percent < 100 ? `Uploading… ${upload.percent}%` : "Processing the video…"}
            </span>
            {/* Once every byte is sent the server is already saving it — a
                cancel then would leave the site showing a video the form does not. */}
            {upload.percent < 100 && (
              <AppButton type="button" variant="ghost" size="xs" onClick={() => abortRef.current?.abort()}>
                Cancel
              </AppButton>
            )}
          </div>
        </div>
      ) : current?.url ? (
        <>
          <video
            key={current.url}
            src={current.url}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => setPlayerDuration(e.currentTarget.duration || 0)}
            className="tw-block tw-aspect-video tw-w-full tw-rounded-sm tw-bg-surface-dark"
          />
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-text-content">
            <Film size={16} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
            <span className="tw-min-w-0 tw-flex-1 tw-truncate" title={current.fileName}>
              {current.fileName || "Preview video"}
            </span>
            {meta && <span className="tw-shrink-0 tw-text-xs tw-text-content-subtle">{meta}</span>}
          </div>
          {pendingFile ? (
            <Callout tone="info">Uploads when you save the course.</Callout>
          ) : (
            <Callout tone="success">
              Live on your website — this course&apos;s page shows a &ldquo;Watch preview&rdquo; button.
            </Callout>
          )}
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton type="button" variant="outline" size="sm" onClick={openPicker} disabled={busy}>
              <RefreshCw size={16} /> Replace
            </AppButton>
            <AppButton type="button" variant="ghost-destructive" size="sm" onClick={handleRemove} disabled={busy}>
              <Trash2 size={16} /> Remove
            </AppButton>
          </div>
        </>
      ) : (
        <>
          <DropZone
            state={dragActive ? "active" : "idle"}
            disabled={busy}
            onClick={openPicker}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              pick(e.dataTransfer.files?.[0]);
            }}
          >
            <UploadCloud
              size={28}
              strokeWidth={1.5}
              aria-hidden="true"
              className="tw-text-content-muted group-hover:tw-text-primary"
            />
            <span className="tw-text-sm tw-font-medium tw-text-content">Drop a video here or click to browse</span>
            <span className="tw-text-xs tw-text-content-subtle">
              MP4, WebM or MOV · up to {PREVIEW_VIDEO_MAX_MB} MB
            </span>
          </DropZone>
          <p className="tw-m-0 tw-text-xs tw-leading-normal tw-text-content-subtle">
            Keep it short — one to three minutes of the course at its best. Visitors can watch it
            without signing up.
          </p>
        </>
      )}

      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <input
        ref={inputRef}
        type="file"
        accept={PREVIEW_VIDEO_ACCEPT}
        className="tw-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          pick(file);
        }}
      />

      <ConfirmModal
        show={confirmRemove}
        onHide={() => !removing && setConfirmRemove(false)}
        onConfirm={confirmRemoval}
        title="Remove the preview video?"
        message="It comes off this course's website page straight away, and the file is deleted."
        confirmText="Remove video"
        variant="danger"
        loading={removing}
      />
    </div>
  );
}
